import React from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";
import { ExplainerSection, WatchExplainerButton } from "@/components/ExplainerVideo";
import { CustomerReview } from "@/components/CustomerReview";
import { StoryCard, STORIES } from "@/components/RoleplayStory";
import {
  Sparkles, Swords, Brain, LineChart, Mic, Trophy, ArrowRight, Check, Quote
} from "lucide-react";

const features = [
  { icon: Swords, title: "Realistic Roleplay", desc: "Practice against 14 AI customers — rude, skeptical, in a hurry, vulnerable. No two conversations alike." },
  { icon: Brain, title: "Coaching from Linden", desc: "An AI trained on Linden's philosophy. He asks questions, never just hands you answers." },
  { icon: LineChart, title: "Instant Analysis", desc: "Every rep scored on opening, rapport, objections, closing — with an action plan." },
  { icon: Mic, title: "Talk Naturally", desc: "Voice mode that listens, responds, and reacts like a real customer." },
  { icon: Trophy, title: "Gamified Growth", desc: "XP, levels, streaks, badges and leaderboards that make practice addictive." },
  { icon: Sparkles, title: "It Remembers You", desc: "Linden tracks your strengths, weaknesses and goals — and coaches you differently over time." },
];

export default function Landing() {
  const navigate = useNavigate();
  const { user } = useAuth();

  return (
    <div className="min-h-screen grid-bg text-slate-900" style={{ background: "#F4F7FB" }}>
      <header className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-5 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <div className="w-9 h-9 rounded-xl bg-emerald-500 flex items-center justify-center glow-emerald">
            <Sparkles className="w-5 h-5 text-black" />
          </div>
          <span className="font-display font-extrabold text-lg">Pitch Craft</span>
        </div>
        <div className="flex items-center gap-3">
          <Link to="/login" className="text-sm text-slate-600 hover:text-slate-900 transition-colors" data-testid="header-login-link">Sign in</Link>
          <Button onClick={() => navigate(user ? "/app" : "/register")} className="bg-emerald-500 hover:bg-emerald-600 text-black font-semibold rounded-full px-5" data-testid="header-cta-btn">
            {user ? "Open App" : "Start Free"}
          </Button>
        </div>
      </header>

      <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 pt-12 md:pt-20 pb-16 grid lg:grid-cols-2 gap-12 items-center">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-emerald-500/30 bg-emerald-500/10 text-emerald-600 text-xs font-mono uppercase tracking-widest mb-6">
            <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" /> Your 24/7 sales trainer
          </div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight leading-[1.05]">
            Become a great salesperson by <span className="text-emerald-600">practicing</span>, not watching.
          </h1>
          <p className="mt-6 text-base sm:text-lg text-slate-500 max-w-xl leading-relaxed">
            Pitch Craft lets you run hundreds of realistic sales conversations with an AI customer — and get instant, personal coaching from an AI version of Linden Burt.
          </p>
          <div className="mt-8 flex flex-wrap gap-3">
            <Button onClick={() => navigate("/register")} className="bg-emerald-500 hover:bg-emerald-600 text-black font-semibold rounded-full px-7 h-12 text-base" data-testid="hero-start-btn">
              Start practicing free <ArrowRight className="w-4 h-4 ml-1" />
            </Button>
            <Button onClick={() => navigate("/login")} variant="outline" className="rounded-full px-7 h-12 text-base border-slate-300 bg-transparent text-slate-900 hover:bg-slate-100" data-testid="hero-signin-btn">
              I have an account
            </Button>
            <WatchExplainerButton variant="ghost" label="Watch the 60-second tour" className="rounded-full px-5 h-12 text-base text-slate-700 hover:text-emerald-700 hover:bg-emerald-500/10" testId="hero-watch-video-btn" />
          </div>
          <div className="mt-8 flex items-center gap-6 text-sm text-slate-500">
            <span className="flex items-center gap-2"><Check className="w-4 h-4 text-emerald-600" /> No card required</span>
            <span className="flex items-center gap-2"><Check className="w-4 h-4 text-emerald-600" /> 14 AI customers</span>
          </div>
        </motion.div>

        <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.6, delay: 0.15 }} className="relative">
          <div className="absolute -inset-4 bg-emerald-500/10 blur-3xl rounded-full" />
          <div className="relative rounded-3xl overflow-hidden border border-slate-200 card-surface">
            <img src="https://static.prod-images.emergentagent.com/jobs/1a9e1561-ca18-4dbb-8f52-13e836a03554/images/063325646653cf8a151ee440b3e604a7efe09ddef4648003466f156b1d0b378f.jpeg" alt="Linden coach" className="w-full h-72 object-cover object-top" />
            <div className="p-5">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 rounded-full bg-emerald-500 flex items-center justify-center text-black font-bold">L</div>
                <div>
                  <div className="font-semibold text-slate-900">Linden</div>
                  <div className="text-xs text-emerald-600 font-mono">COACHING · LIVE</div>
                </div>
              </div>
              <p className="text-sm text-slate-600 leading-relaxed">"Good. But you jumped to the price too fast. What could you have asked <em>before</em> she brought up cost? Let's run it again."</p>
            </div>
          </div>
        </motion.div>
      </section>

      <ExplainerSection />

      <CustomerReview />

      <section id="managers" className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 pb-16 scroll-mt-24" data-testid="managers-section">
        <div className="mb-6">
          <h2 className="text-2xl sm:text-3xl font-bold tracking-tight">Running a sales team? This is your remote training room.</h2>
          <p className="text-slate-500 mt-3 max-w-2xl">Invite your reps with one code. They practise real objections on their own time — you get a live team table, scores, streaks and every shared scorecard, wherever you are.</p>
        </div>
        <StoryCard story={STORIES.manager} cta="Set up my team free →" onCta={() => navigate("/register")} testId="landing-manager-story" />
      </section>

      <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <h2 className="text-2xl sm:text-3xl font-bold tracking-tight text-center">Everything you need to close more deals</h2>
        <p className="text-slate-500 text-center mt-3 max-w-2xl mx-auto">People don't get better by memorising scripts. They get better through repetition, feedback, and a coach who pushes them.</p>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 mt-10">
          {features.map((f, i) => (
            <motion.div key={f.title} initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.05 }}
              className="p-6 rounded-2xl card-surface hover:border-emerald-500/30 transition-colors group">
              <div className="w-11 h-11 rounded-xl bg-emerald-500/10 border border-emerald-500/20 flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
                <f.icon className="w-5 h-5 text-emerald-600" />
              </div>
              <h3 className="font-semibold text-lg text-slate-900">{f.title}</h3>
              <p className="text-sm text-slate-500 mt-2 leading-relaxed">{f.desc}</p>
            </motion.div>
          ))}
        </div>
      </section>

      <section className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="rounded-3xl card-surface p-8 sm:p-12 text-center relative overflow-hidden">
          <Quote className="w-10 h-10 text-emerald-500/40 mx-auto mb-4" />
          <p className="text-xl sm:text-2xl font-display font-semibold text-slate-900 leading-snug">"Mindset before technique. Persistence conquers resistance. Every objection contains information."</p>
          <p className="mt-4 text-sm text-emerald-600 font-mono uppercase tracking-widest">— The Linden Philosophy</p>
        </div>
      </section>

      <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-16 text-center">
        <h2 className="text-3xl sm:text-4xl font-extrabold tracking-tight">Your best rep is one conversation away.</h2>
        <Button onClick={() => navigate("/register")} className="mt-8 bg-emerald-500 hover:bg-emerald-600 text-black font-semibold rounded-full px-8 h-12 text-base" data-testid="footer-cta-btn">
          Start your first roleplay <ArrowRight className="w-4 h-4 ml-1" />
        </Button>
      </section>

      <footer className="border-t border-slate-200 py-8 text-center text-sm text-slate-600">
        © 2026 Pitch Craft · The world's most realistic AI sales coach · <Link to="/privacy" className="hover:text-slate-900 underline-offset-2 hover:underline" data-testid="footer-privacy-link">Privacy Policy</Link> · <Link to="/terms" className="hover:text-slate-900 underline-offset-2 hover:underline" data-testid="footer-terms-link">Terms of Service</Link>
      </footer>
    </div>
  );
}
